/**
 * packages/ui-shell/src/components/SiteGuideSection.ts
 * Google AdSense審査対策 & SEO対策用 詳細解説＆FAQセクション
 * 多言語対応 (日本語/英語/他)
 */
import { i18n, subscribeLocaleChange } from '../i18n/i18n';

export class SiteGuideSection {
  private element: HTMLElement;

  constructor() {
    this.element = document.createElement('section');
    this.element.id = 'siteGuideSection';
    this.element.className = 'site-guide-container';
    this.render();

    // 言語切り替え時に再描画
    subscribeLocaleChange(() => {
      this.render();
    });
  }

  getElement(): HTMLElement {
    return this.element;
  }

  private render(): void {
    const currentLocale = i18n.getLocale();
    if (currentLocale === 'ja') {
      this.renderJapanese();
    } else {
      this.renderEnglish();
    }
  }

  private renderJapanese(): void {
    this.element.innerHTML = `
      <div class="site-guide-inner">
        <!-- 1. アプリの概要と独自性 -->
        <article class="guide-block">
          <div class="guide-badge">About Jiggly Touch</div>
          <h2 class="guide-title">触れるぷるぷる画像物理シミュレーター「Jiggly Touch」とは？</h2>
          <p class="guide-lead">
            Jiggly Touch（ジグリータッチ）は、お気に入りのイラストや写真、ペットの画像などをWebブラウザ上でリアルタイムにメッシュ化し、まるで生きているかのように指先やつまみ操作で「ぷるぷる・ぽよぽよ」と弾ませることができるインタラクティブWebアプリケーションです。
          </p>
          <p class="guide-text">
            従来の静止画を見るだけのWeb体験とは異なり、ブラウザ上でリアルタイム物理演算（マス・スプリング・ダンパーモデル）を実行することで、画像の弾力、跳ね返りの強さ、共鳴時間などを自由にカスタマイズしながら触覚的な心地よさを体験できます。
          </p>
        </article>

        <!-- 2. 主な特徴 -->
        <article class="guide-block">
          <h3 class="guide-subtitle">🌟 Jiggly Touch の4大特徴</h3>
          <div class="features-grid">
            <div class="feature-card">
              <div class="feature-icon">🔒</div>
              <h4>100% 端末内処理で安心・安全</h4>
              <p>
                読み込んだ画像は外部サーバーへ一切送信・保存されません。すべての画像処理と物理計算は、お使いのスマートフォンやPCのブラウザ（ローカルメモリ）内のみで完結するため、プライベートな写真やオリジナルのイラストも安心してお使いいただけます。
              </p>
            </div>

            <div class="feature-card">
              <div class="feature-icon">⚡</div>
              <h4>リアルタイム触覚物理シミュレーション</h4>
              <p>
                画面をタップしたりドラッグすると、触れた箇所を中心に滑らかな変形と波紋が発生します。影響範囲（半径）、弾力（復元力）、減衰（共鳴の長さ）のスライダーを直感的に動かすだけで、ゼリーのような柔らかさからゴムのような強い弾力まで自由自在に再現可能です。
              </p>
            </div>

            <div class="feature-card">
              <div class="feature-icon">🎬</div>
              <h4>GIF / MP4 高画質アニメーション書き出し</h4>
              <p>
                触って揺らしたアニメーションは、ワンクリックでMP4動画やループGIFとして端末にダウンロード保存できます。𝕏（Twitter）やSNSへのシェア機能も備えており、作った楽しいアニメーションを簡単にフォロワーと共有できます。
              </p>
            </div>

            <div class="feature-card">
              <div class="feature-icon">📋</div>
              <h4>クリップボード直接貼り付け（Ctrl + V）対応</h4>
              <p>
                Web上で気になった画像を右クリックして「画像をコピー」し、当サイト上で「Ctrl + V」を押すだけで、端末に保存する手間なく一瞬でキャンバスに取り込んでぷるぷる触ることができます。
              </p>
            </div>
          </div>
        </article>

        <!-- 3. 簡単4ステップ使い方ガイド -->
        <article class="guide-block">
          <h3 class="guide-subtitle">📖 かんたん使い方ステップ</h3>
          <div class="steps-container">
            <div class="step-item">
              <div class="step-number">STEP 1</div>
              <div class="step-content">
                <h4>画像を選ぶ</h4>
                <p>画面下のサンプルアイコン（女の子、プリン、桃、肉球）をクリックするか、左上の「画像を開く」からお好きなイラストや写真を読み込みます。Web上の画像を右クリックして「Ctrl + V」で貼り付けることも可能です。</p>
              </div>
            </div>

            <div class="step-item">
              <div class="step-number">STEP 2</div>
              <div class="step-content">
                <h4>触って引っ張る・弾ませる</h4>
                <p>画像の上をマウスや指でクリック・ドラッグして引っ張ると、ぷるんと弾みます。画面上の「👉 画像を突く」ボタンを押すと、中央に自動でインパクトを与えることもできます。</p>
              </div>
            </div>

            <div class="step-item">
              <div class="step-number">STEP 3</div>
              <div class="step-content">
                <h4>物理パラメータを好みに調整する</h4>
                <p>右側の「ぷるぷる物理」パネルを開き、スライダーを動かして弾力や影響範囲を調整します。お気に入りの揺れ具合が見つかったら「プリセット保存」でいつでも呼び出せます。</p>
              </div>
            </div>

            <div class="step-item">
              <div class="step-number">STEP 4</div>
              <div class="step-content">
                <h4>動画やGIFとして書き出してシェアする</h4>
                <p>ヘッダーの「エクスポート」ボタンを押し、フォーマット（MP4またはGIF）と秒数を選んで「録画スタート」を押します。3秒のカウントダウン後に画面を触ると、その動きがそのまま録画され、自動でダウンロードされます。</p>
              </div>
            </div>
          </div>
        </article>

        <!-- 4. おすすめの活用シーン -->
        <article class="guide-block">
          <h3 class="guide-subtitle">💡 こんな画像で遊ぶのがおすすめ！</h3>
          <ul class="usecases-list">
            <li><strong>アニメ・イラストのキャラクター：</strong> ほっぺや髪の毛、衣装の柔らかい部分をつまんで愛らしいリアクションを楽しむ。</li>
            <li><strong>ペットや動物の写真：</strong> 犬や猫の肉球、ハムスターの膨らんだほっぺ、ぷにぷにしたお腹を触って癒やされる。</li>
            <li><strong>スイーツ・食べ物：</strong> プリン、パンケーキ、ゼリー、オムライスなどをタップしてリアルな弾力を楽しむ。</li>
            <li><strong>SNSでのシェア・ショート動画作成：</strong> 自分で描いたイラストをぷるぷる動かして、𝕏やTikTok用のショート動画として書き出す。</li>
          </ul>
        </article>

        <!-- 5. よくある質問 (FAQ) -->
        <article class="guide-block">
          <h3 class="guide-subtitle">❓ よくある質問 (FAQ)</h3>
          <div class="faq-list">
            <details class="faq-item" open>
              <summary class="faq-question">Q. 利用料金はかかりますか？</summary>
              <div class="faq-answer">
                <p>A. 基本機能（画像の読み込み、物理シミュレーション、パラメータ調整、動画・GIF書き出し、SNS共有）はすべて完全無料でお使いいただけます。透かしロゴの除去や追加効果音をご希望の方向けにPro版もご用意しています。</p>
              </div>
            </details>

            <details class="faq-item" open>
              <summary class="faq-question">Q. アップロードした画像が外部に漏れたり流出する心配はありませんか？</summary>
              <div class="faq-answer">
                <p>A. 一切ありません。本アプリは画像を外部サーバーへ送信するAPIを一切持たず、HTML5 File API と Canvas / WebGL 技術により、お客様の端末（ブラウザ）のメモリ上でのみ処理されます。安全にご利用ください。</p>
              </div>
            </details>

            <details class="faq-item" open>
              <summary class="faq-question">Q. スマートフォンやタブレットでも遊べますか？</summary>
              <div class="faq-answer">
                <p>A. はい、iPhone（iOS Safari）、Android（Chrome）などの主要なモバイルブラウザに完全対応しています。マルチタッチやスワイプ操作で直感的にぷるぷる体験をお楽しみいただけます。</p>
              </div>
            </details>

            <details class="faq-item" open>
              <summary class="faq-question">Q. 書き出した動画やGIFはSNSに投稿しても大丈夫ですか？</summary>
              <div class="faq-answer">
                <p>A. はい、大歓迎です！エクスポート機能で保存されたMP4やGIFファイルは、𝕏（Twitter）、Instagram、TikTok、YouTube Shortsなど、お好きなプラットフォームへ自由に投稿いただけます。ハッシュタグ「#JigglyTouch」を付けてシェアしていただけると幸いです。</p>
              </div>
            </details>
          </div>
        </article>

        <!-- 6. フッター法的リンク -->
        <footer class="guide-footer-links">
          <a href="/privacy.html">プライバシーポリシー</a>
          <span>•</span>
          <a href="/terms.html">利用規約</a>
          <span>•</span>
          <a href="https://x.com/KILROYSANWHERE" target="_blank" rel="noopener noreferrer">開発者へのお問い合わせ (𝕏 @KILROYSANWHERE)</a>
          <p class="copyright-note">© Jiggly Touch. All rights reserved.</p>
        </footer>
      </div>
    `;
  }

  private renderEnglish(): void {
    this.element.innerHTML = `
      <div class="site-guide-inner">
        <!-- 1. App Overview & Uniqueness -->
        <article class="guide-block">
          <div class="guide-badge">About Jiggly Touch</div>
          <h2 class="guide-title">What is Jiggly Touch? Interactive Squishy Physics Simulator</h2>
          <p class="guide-lead">
            Jiggly Touch is an interactive web-based tactile physics simulator that dynamically turns any illustration, photo, or pet picture into a squishy, springy elastic mesh right inside your browser. Pinch, pull, and wobble your favorite images with satisfying organic physics!
          </p>
          <p class="guide-text">
            Unlike static image viewers, Jiggly Touch computes real-time mass-spring-damper physics algorithms directly in your browser. You can freely customize bounce stiffness, damping resonance, and deformation radius to experience authentic tactile joy on any device.
          </p>
        </article>

        <!-- 2. Core Features -->
        <article class="guide-block">
          <h3 class="guide-subtitle">🌟 4 Key Features of Jiggly Touch</h3>
          <div class="features-grid">
            <div class="feature-card">
              <div class="feature-icon">🔒</div>
              <h4>100% Client-Side Private & Secure</h4>
              <p>
                Your images are never uploaded or sent to any remote server. All image decoding and physics simulation run entirely within your local device's browser memory (HTML5 File API & WebGL), guaranteeing 100% privacy for original artwork and private photos.
              </p>
            </div>

            <div class="feature-card">
              <div class="feature-icon">⚡</div>
              <h4>Real-Time Tactile Physics Simulation</h4>
              <p>
                Touch, click, or drag anywhere on the image to generate organic deformation ripples. Using simple intuitive sliders, you can adjust elasticity, resonance damping, and radius of effect from jelly-like jiggle to high-tension rubber bounce.
              </p>
            </div>

            <div class="feature-card">
              <div class="feature-icon">🎬</div>
              <h4>High-Quality MP4 & GIF Export</h4>
              <p>
                Record your interactive squishy sessions with 1 click and download them as smooth MP4 videos or looping animated GIFs. Built-in 𝕏 (Twitter) and social media share tools let you effortlessly showcase your animations to friends and followers.
              </p>
            </div>

            <div class="feature-card">
              <div class="feature-icon">📋</div>
              <h4>Instant Clipboard Paste (Ctrl + V)</h4>
              <p>
                Right-click any image on the web, click "Copy image", and press [Ctrl + V] on Jiggly Touch. The image loads instantly onto the canvas without having to download or save files to your local drive first.
              </p>
            </div>
          </div>
        </article>

        <!-- 3. 4-Step Quick Start Guide -->
        <article class="guide-block">
          <h3 class="guide-subtitle">📖 Quick 4-Step User Guide</h3>
          <div class="steps-container">
            <div class="step-item">
              <div class="step-number">STEP 1</div>
              <div class="step-content">
                <h4>Choose an Image</h4>
                <p>Click any preset icon at the bottom of the screen (Anime Girl, Custard Pudding, Peach, Cat Paw), open your own file from the top-left button, or paste an image directly from your clipboard using Ctrl + V.</p>
              </div>
            </div>

            <div class="step-item">
              <div class="step-number">STEP 2</div>
              <div class="step-content">
                <h4>Pinch, Drag & Wobble</h4>
                <p>Click or touch and drag anywhere on the canvas to stretch and bounce the image. You can also click the "👉 Poke Image" button to trigger an instant impact impulse at the center.</p>
              </div>
            </div>

            <div class="step-item">
              <div class="step-number">STEP 3</div>
              <div class="step-content">
                <h4>Fine-Tune Physics Parameters</h4>
                <p>Open the "Physics" panel on the right side to tweak bounce stiffness, damping duration, and radius of effect. Save your favorite tactile feel as a preset to restore it anytime.</p>
              </div>
            </div>

            <div class="step-item">
              <div class="step-number">STEP 4</div>
              <div class="step-content">
                <h4>Record, Export & Share</h4>
                <p>Click "Export" in the header, choose MP4 or GIF with your desired duration, and hit "Start Recording". Touch and play with the image during recording, and the video will automatically download to your device!</p>
              </div>
            </div>
          </div>
        </article>

        <!-- 4. Recommended Ideas -->
        <article class="guide-block">
          <h3 class="guide-subtitle">💡 Fun Image Ideas to Try</h3>
          <ul class="usecases-list">
            <li><strong>Anime & Manga Characters:</strong> Pinch sulking puffy cheeks, soft hair, or clothing for adorable expressive reactions.</li>
            <li><strong>Cute Pets & Animals:</strong> Squeeze squishy cat paws, chubby hamster cheeks, or fluffy puppy bellies.</li>
            <li><strong>Desserts & Food:</strong> Poke custard flan, pancakes, jelly, or soufflés to experience lifelike organic jiggle.</li>
            <li><strong>Social Media Content:</strong> Create viral short clips and GIFs from your own character illustrations for 𝕏, TikTok, and Instagram.</li>
          </ul>
        </article>

        <!-- 5. Frequently Asked Questions (FAQ) -->
        <article class="guide-block">
          <h3 class="guide-subtitle">❓ Frequently Asked Questions (FAQ)</h3>
          <div class="faq-list">
            <details class="faq-item" open>
              <summary class="faq-question">Q. Is Jiggly Touch free to use?</summary>
              <div class="faq-answer">
                <p>A. Yes! All core features—including custom image loading, full real-time physics simulation, parameter adjustment, MP4/GIF export, and social sharing—are 100% free. A Pro edition is available for users who wish to remove the watermark logo and unlock ambient desktop features.</p>
              </div>
            </details>

            <details class="faq-item" open>
              <summary class="faq-question">Q. Are my uploaded photos or illustrations uploaded to your servers?</summary>
              <div class="faq-answer">
                <p>A. Absolutely not. Jiggly Touch does not have any backend upload APIs. All image decoding and mesh calculations happen strictly inside your browser's local memory via WebGL and HTML5 Canvas. Your images never leave your device.</p>
              </div>
            </details>

            <details class="faq-item" open>
              <summary class="faq-question">Q. Does it work on smartphones and tablets?</summary>
              <div class="faq-answer">
                <p>A. Yes, Jiggly Touch is fully responsive and supports touch gestures on iOS Safari, iPadOS, Android Chrome, and mobile browsers with smooth multitouch response.</p>
              </div>
            </details>

            <details class="faq-item" open>
              <summary class="faq-question">Q. Can I post exported videos and GIFs to social media?</summary>
              <div class="faq-answer">
                <p>A. Yes, absolutely! You are free to share your exported MP4 videos and animated GIFs on 𝕏 (Twitter), TikTok, Instagram, Discord, and YouTube Shorts. Tagging #JigglyTouch is always appreciated!</p>
              </div>
            </details>
          </div>
        </article>

        <!-- 6. Footer Legal Links -->
        <footer class="guide-footer-links">
          <a href="/privacy.html">Privacy Policy</a>
          <span>•</span>
          <a href="/terms.html">Terms of Service</a>
          <span>•</span>
          <a href="https://x.com/KILROYSANWHERE" target="_blank" rel="noopener noreferrer">Contact Developer (𝕏 @KILROYSANWHERE)</a>
          <p class="copyright-note">© Jiggly Touch. All rights reserved.</p>
        </footer>
      </div>
    `;
  }
}
